import React from 'react';
import { ArrowRight, Clock, Target, Shield } from 'lucide-react';

const Hero = () => {
  return (
    <section id="hero" className="pt-24 pb-20 bg-white min-h-screen flex items-center">
      <div className="container-center">
        <div className="grid lg:grid-cols-2 gap-12 items-center px-4 sm:px-6 lg:px-8">
          {/* Left Content */}
          <div className="space-y-8 animate-slide-in-left">
            {/* Trust Badge */}
            <div className="inline-flex items-center space-x-2 bg-burnt-orange/10 px-4 py-2 rounded-full">
              <Shield className="h-4 w-4 text-burnt-orange" />
              <span className="text-sm font-semibold text-burnt-orange">
                Discreet support • Limited client slots • Built for founders & executives
              </span>
            </div>

            {/* Main Headline */}
            <div className="space-y-4">
              <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-olive-brown leading-tight">
                Get your time back—without hiring full-time.
              </h1>
              <div className="w-24 h-1 bg-burnt-orange rounded-full"></div>
            </div>

            {/* Subheadline */}
            <div className="space-y-6">
              <h2 className="text-base sm:text-lg md:text-xl lg:text-2xl font-medium text-olive-brown/80">
                FractionalVa supports busy leaders with calm, reliable execution: inbox, calendar, follow-through, and the daily details that keep stealing your focus.
              </h2>
              <p className="text-base sm:text-lg font-semibold text-burnt-orange">
                Less chaos. More progress. You lead—we handle the rest.
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <a
                href="#contact"
                className="btn-primary group inline-flex items-center justify-center"
                aria-label="Book a Quick Call - See if it's a fit in 15 minutes"
                title="See if it's a fit in 15 minutes"
              >
                Book a Quick Call
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
              </a>
              <a
                href="#contact"
                className="btn-secondary inline-flex items-center justify-center"
                aria-label="Request a Confidential Memo - We'll map how support would work for your role"
                title="We'll map how support would work for your role"
              >
                Request a Confidential Memo
              </a>
            </div>

            {/* Social Proof */}
            <div className="pt-8 border-t border-olive-brown/20">
              <div className="space-y-3">
                <div className="text-sm font-medium text-olive-brown/80">Quick proof from our clients:</div>
                <div className="space-y-2">
                  <p className="text-sm text-olive-brown/70">"• My week finally feels controlled again."</p>
                  <p className="text-sm text-olive-brown/70">"• We stopped dropping balls across projects."</p>
                  <p className="text-sm text-olive-brown/70">"• I gained hours back without losing momentum."</p>
                </div>
                <p className="text-xs text-olive-brown/60 italic">
                  Results vary by role, workload, and scope. We'll confirm what's realistic during onboarding.
                </p>
              </div>
            </div>
          </div>

          {/* Right Content - Visual Elements */}
          <div className="relative animate-slide-in-right">
            <div className="relative">
              {/* Main Visual Container */}
              <div className="bg-gradient-to-br from-burnt-orange/20 to-olive-brown/20 rounded-3xl p-8 backdrop-blur-sm">
                <div className="space-y-6">
                  {/* Calendar Control */}
                  <div className="bg-white/90 rounded-2xl p-6 card-shadow animate-bounce-subtle">
                    <div className="flex items-center space-x-4">
                      <div className="p-3 bg-burnt-orange/20 rounded-full">
                        <Clock className="h-8 w-8 text-burnt-orange" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-olive-brown text-base sm:text-lg">Calendar Control</h3>
                        <p className="text-olive-brown/70 text-xs sm:text-sm">Fewer interruptions. Cleaner priorities.</p>
                      </div>
                    </div>
                  </div>

                  {/* Inbox Clarity */}
                  <div className="bg-white/90 rounded-2xl p-6 card-shadow ml-8" style={{animationDelay: '0.2s'}}>
                    <div className="flex items-center space-x-4">
                      <div className="p-3 bg-burnt-orange/20 rounded-full">
                        <Target className="h-8 w-8 text-burnt-orange" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-olive-brown text-base sm:text-lg">Inbox Clarity</h3>
                        <p className="text-olive-brown/70 text-xs sm:text-sm">Less noise. Better decisions.</p>
                      </div>
                    </div>
                  </div>

                  {/* Follow-Through & Execution */}
                  <div className="bg-white/90 rounded-2xl p-6 card-shadow" style={{animationDelay: '0.4s'}}>
                    <div className="flex items-center space-x-4">
                      <div className="p-3 bg-burnt-orange/20 rounded-full">
                        <Shield className="h-8 w-8 text-burnt-orange" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-olive-brown text-base sm:text-lg">Follow-Through & Execution</h3>
                        <p className="text-olive-brown/70 text-xs sm:text-sm">No more "Did that get done?"</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Elements */}
              <div className="absolute -top-4 -right-4 w-20 h-20 bg-burnt-orange/20 rounded-full animate-pulse"></div>
              <div className="absolute -bottom-6 -left-6 w-16 h-16 bg-olive-brown/20 rounded-full animate-pulse" style={{animationDelay: '1s'}}></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;